import React, { useState, useEffect } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    LineChart,
    Line,
    Cell,
} from "recharts";
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../app/z-store/store';
import { getBudgets } from "../../../../../utils/urls";

function BudgetStatisticsTab() {
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statistics, setStatistics] = useState({
        totalBudget: 0,
        totalBudgets: 0,
        approvedBudgets: 0,
        pendingBudgets: 0,
        rejectedBudgets: 0,
        inProgressBudgets: 0
    });

    // Fetch budgets when facility changes
    useEffect(() => {
        if (selectedFacilityId) {
            fetchBudgets();
        }
    }, [selectedFacilityId]);

    // Fetch budgets from API
    const fetchBudgets = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${getBudgets}/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                const budgetData = response.data?.budgets || [];
                setBudgets(budgetData);
                calculateStatistics(budgetData);
            } else {
                console.error("Failed to fetch budgets:", response.error);
                setBudgets([]);
            }
        } catch (error) {
            console.error("Error fetching budgets:", error);
            setBudgets([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate statistics from budget data
    const calculateStatistics = (budgetData) => {
        const stats = {
            totalBudget: budgetData.reduce((sum, budget) => sum + (budget.allocation || 0), 0),
            totalBudgets: budgetData.length,
            approvedBudgets: budgetData.filter(b => b.approvalStatus === 'approved').length,
            pendingBudgets: budgetData.filter(b => b.approvalStatus === 'pending').length,
            rejectedBudgets: budgetData.filter(b => b.approvalStatus === 'rejected').length,
            inProgressBudgets: budgetData.filter(b => b.approvalStatus === 'in_progress').length
        };
        setStatistics(stats);
    };

    // Prepare data for department breakdown chart
    const getDepartmentData = () => {
        const departmentMap = {};
        budgets.forEach(budget => {
            const dept = budget.department || 'Unknown';
            if (!departmentMap[dept]) {
                departmentMap[dept] = {
                    department: dept,
                    totalBudget: 0,
                    count: 0
                };
            }
            departmentMap[dept].totalBudget += budget.allocation || 0;
            departmentMap[dept].count += 1;
        });
        return Object.values(departmentMap);
    };

    // Prepare data for status pie chart
    const getStatusData = () => {
        return [
            { name: 'Approved', value: statistics.approvedBudgets, color: '#28a745' },
            { name: 'Pending', value: statistics.pendingBudgets, color: '#ffc107' },
            { name: 'In Progress', value: statistics.inProgressBudgets, color: '#17a2b8' },
            { name: 'Rejected', value: statistics.rejectedBudgets, color: '#dc3545' }
        ].filter(item => item.value > 0);
    };

    // Prepare data for monthly trend (based on creation dates)
    const getMonthlyTrendData = () => {
        const monthlyMap = {};
        
        budgets.forEach(budget => {
            const date = new Date(budget.createdAt || budget.startDate);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
            
            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = {
                    month: monthName,
                    budgets: 0,
                    allocation: 0
                };
            }
            monthlyMap[monthKey].budgets += 1;
            monthlyMap[monthKey].allocation += budget.allocation || 0;
        });

        return Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(value);
    };

    // Custom tooltip for currency formatting
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border rounded shadow">
                    <p className="mb-1">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="mb-0" style={{ color: entry.color }}>
                            {`${entry.dataKey}: ${
                                entry.dataKey.includes('allocation') || entry.dataKey.includes('Budget')
                                    ? formatCurrency(entry.value)
                                    : entry.value
                            }`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // If no facility is selected
    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    Please select a facility to view budget statistics.
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading budget statistics...</p>
                </div>
            </div>
        );
    }

    // No data state
    if (budgets.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <i className="ti ti-chart-bar" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                    <h5 className="mt-3">No Budget Data Available</h5>
                    <p className="text-muted">Create some budgets to view statistics and analytics.</p>
                </div>
            </div>
        );
    }

    const departmentData = getDepartmentData();
    const statusData = getStatusData();
    const monthlyData = getMonthlyTrendData();

    return (
        <div className="row">
            <div className="col-lg-12 col-xxl-12">
                <div className="card">
                    <div className="card-body position-relative">
                        {/* Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h6>Total Budget Allocation</h6>
                                        <h3>{formatCurrency(statistics.totalBudget)}</h3>
                                        <small>{statistics.totalBudgets} Budget{statistics.totalBudgets !== 1 ? 's' : ''}</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-success text-white">
                                    <div className="card-body">
                                        <h6>Approved Budgets</h6>
                                        <h3>{statistics.approvedBudgets}</h3>
                                        <small>{statistics.totalBudgets > 0 ? Math.round((statistics.approvedBudgets / statistics.totalBudgets) * 100) : 0}% of total</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-warning text-white">
                                    <div className="card-body">
                                        <h6>Pending Approval</h6>
                                        <h3>{statistics.pendingBudgets + statistics.inProgressBudgets}</h3>
                                        <small>{statistics.pendingBudgets} pending, {statistics.inProgressBudgets} in progress</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-danger text-white">
                                    <div className="card-body">
                                        <h6>Rejected Budgets</h6>
                                        <h3>{statistics.rejectedBudgets}</h3>
                                        <small>{statistics.totalBudgets > 0 ? Math.round((statistics.rejectedBudgets / statistics.totalBudgets) * 100) : 0}% of total</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Charts Row */}
                        <div className="row">
                            {/* Department Budget Breakdown */}
                            {departmentData.length > 0 && (
                                <div className="col-md-8">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Budget Allocation by Department</h5>
                                        </div>
                                        <div className="card-body">
                                            <div style={{ height: "300px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={departmentData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="department" />
                                                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar dataKey="totalBudget" fill="#0d6efd" name="Budget Allocation" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Status Distribution */}
                            {statusData.length > 0 && (
                                <div className="col-md-4">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Budget Status Distribution</h5>
                                        </div>
                                        <div className="card-body">
                                            <div style={{ height: "300px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={statusData}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                            outerRadius={80}
                                                            fill="#8884d8"
                                                            dataKey="value"
                                                        >
                                                            {statusData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Monthly Trend */}
                        {monthlyData.length > 1 && (
                            <div className="row mt-4">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Budget Creation Trend</h5>
                                        </div>
                                        <div className="card-body">
                                            <div style={{ height: "300px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={monthlyData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="month" />
                                                        <YAxis yAxisId="left" orientation="left" />
                                                        <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatCurrency(value)} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Legend />
                                                        <Bar yAxisId="left" dataKey="budgets" fill="#17a2b8" name="Number of Budgets" />
                                                        <Line yAxisId="right" type="monotone" dataKey="allocation" stroke="#dc3545" name="Total Allocation" strokeWidth={2} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Department Summary Table */}
                        {departmentData.length > 0 && (
                            <div className="row mt-4">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Department Budget Summary</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Department</th>
                                                            <th>Number of Budgets</th>
                                                            <th>Total Allocation</th>
                                                            <th>Average per Budget</th>
                                                            <th>% of Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {departmentData.map((dept, index) => (
                                                            <tr key={index}>
                                                                <td>{dept.department}</td>
                                                                <td>{dept.count}</td>
                                                                <td>{formatCurrency(dept.totalBudget)}</td>
                                                                <td>{formatCurrency(dept.totalBudget / dept.count)}</td>
                                                                <td>{((dept.totalBudget / statistics.totalBudget) * 100).toFixed(1)}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BudgetStatisticsTab;